// src/client/wakelock.ts — Screen Wake Lock API wrapper.
// Single sentinel at a time. Re-acquires on visibilitychange because the
// OS revokes the lock when the tab is hidden. Feature-detected: Safari iOS
// 16.4+ only.

type WakeLockSentinelLike = { release: () => Promise<void>; released: boolean; addEventListener: (t: string, l: EventListener) => void };

let sentinel: WakeLockSentinelLike | null = null;
let desired = false;
let reAcquiring = false;

interface WakeLockRequester { request: (type?: "screen") => Promise<WakeLockSentinelLike> }

function getWakeLock(): WakeLockRequester | null {
  if (typeof navigator === "undefined") return null;
  // lib.dom.d.ts already includes `wakeLock?: WakeLock` on Navigator in
  // recent TS; cast through unknown to avoid lib-version drift.
  const w = (navigator as unknown as { wakeLock?: WakeLockRequester }).wakeLock;
  return w ?? null;
}

async function acquire(): Promise<void> {
  if (reAcquiring) return;
  if (sentinel) return;
  const w = getWakeLock();
  if (!w) return; // unsupported
  reAcquiring = true;
  try {
    sentinel = await w.request("screen");
    sentinel.addEventListener("release", () => {
      sentinel = null;
      if (desired) void acquire();
    });
  } catch {
    sentinel = null;
  } finally {
    reAcquiring = false;
  }
}

async function release(): Promise<void> {
  if (!sentinel) return;
  try { await sentinel.release(); } catch { /* */ }
  sentinel = null;
}

/**
 * Enable or disable wake lock based on the `enable` flag.
 * Idempotent: calling enable() while already enabled is a no-op.
 */
export async function setEnabled(enable: boolean): Promise<void> {
  desired = enable;
  if (enable) await acquire();
  else await release();
}

export function isEnabled(): boolean {
  return desired;
}

export function isSupported(): boolean {
  return Boolean(getWakeLock());
}

// Re-acquire on tab becoming visible again (the OS silently releases the
// lock when the tab goes background — we have to re-request).
if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible" && desired && !sentinel) {
      void acquire();
    }
  });
}
